import React, { useState, useEffect } from 'react';
import axios from 'axios';

const STATUS_COLORS = {
    Delivered: { bg: '#dcfce7', color: '#16a34a' },
    Confirmed: { bg: '#dbeafe', color: '#2563eb' },
    Pending:   { bg: '#fef9c3', color: '#ca8a04' },
    Draft:     { bg: '#f1f5f9', color: '#64748b' },
    Cancelled: { bg: '#fee2e2', color: '#dc2626' },
};

export default function SalesOrdersList() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('All');

    useEffect(() => {
        axios.get('/api/orders')
            .then(r => setOrders(r.data))
            .catch(() => setOrders([]))
            .finally(() => setLoading(false));
    }, []);

    const filtered = orders.filter(o =>
        (filterStatus === 'All' || o.status === filterStatus) &&
        (o.client.toLowerCase().includes(search.toLowerCase()) ||
         o.id.toLowerCase().includes(search.toLowerCase()) ||
         (o.po || '').toLowerCase().includes(search.toLowerCase()))
    );

    const totalAmount = orders.reduce((a, o) => a + o.amount, 0);
    const delivered = orders.filter(o => o.status === 'Delivered').length;
    const pending = orders.filter(o => o.status === 'Pending' || o.status === 'Confirmed').length;

    const addOrder = async () => {
        const client = prompt('Client Name:');
        const po = prompt('P.O. Number:');
        const amount = prompt('Order Amount (₹):');
        if (client && po) {
            try {
                const res = await axios.post('/api/orders', {
                    client, po,
                    amount: parseFloat(amount) || 0,
                    date: new Date().toLocaleDateString('en-GB').replace(/\//g, '/'),
                    delivery: '', items: 1, status: 'Draft'
                });
                setOrders(prev => [...prev, res.data.order]);
            } catch { alert('Failed to add order'); }
        }
    };

    const deleteOrder = async (id) => {
        if (!window.confirm('Delete this order?')) return;
        try {
            await axios.delete(`/api/orders/${id}`);
            setOrders(prev => prev.filter(o => o.id !== id));
        } catch { alert('Delete failed'); }
    };

    const updateStatus = async (id, status) => {
        try {
            await axios.put(`/api/orders/${id}`, { status });
            setOrders(prev => prev.map(o => o.id === id ? { ...o, status } : o));
        } catch { alert('Update failed'); }
    };

    return (
        <>
            <style>{`
                .so-row { transition: background 0.15s ease; cursor: pointer; }
                .so-row:hover { background: #f8fafc !important; }
                .so-btn { display:inline-flex; align-items:center; gap:6px; padding:8px 16px; border-radius:9px; border:none; font-family:'Inter',sans-serif; font-weight:600; font-size:13px; cursor:pointer; transition:all 0.2s ease; }
                .so-btn:hover { transform:translateY(-1px); }
                .so-badge { padding:4px 12px; border-radius:20px; font-size:11px; font-weight:700; display:inline-block; }
                .search-input { padding:10px 14px 10px 36px; border-radius:9px; border:1.5px solid #e2e8f0; font-size:13px; font-family:'Inter',sans-serif; outline:none; transition:all 0.2s; width:220px; background:#f8fafc; }
                .search-input:focus { border-color:#3b82f6; background:#fff; box-shadow:0 0 0 3px rgba(59,130,246,0.1); }
                .filter-pill { padding:6px 14px; border-radius:20px; border:1.5px solid #e2e8f0; font-size:12px; font-weight:600; cursor:pointer; transition:all 0.2s; background:#fff; color:#64748b; font-family:'Inter',sans-serif; }
                .filter-pill.active { background:#3b82f6; border-color:#3b82f6; color:#fff; }
                .filter-pill:hover:not(.active) { border-color:#94a3b8; background:#f8fafc; }
                .stat-card { background:#fff; border-radius:14px; padding:20px 24px; border:1px solid #f1f5f9; box-shadow:0 2px 12px -4px rgba(0,0,0,0.06); transition:all 0.2s; }
                .stat-card:hover { transform:translateY(-2px); box-shadow:0 8px 24px -6px rgba(0,0,0,0.1); }
            `}</style>

            <div style={{ display:'flex', flexDirection:'column', gap:'20px', fontFamily:'Inter,sans-serif' }}>
                {/* Stats Row */}
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'16px' }}>
                    {[
                        { label:'Total Orders', value: orders.length, icon:'🛍️', color:'#3b82f6', bg:'#eff6ff' },
                        { label:'Delivered', value: delivered, icon:'✅', color:'#16a34a', bg:'#dcfce7' },
                        { label:'In Progress', value: pending, icon:'⏳', color:'#ca8a04', bg:'#fef9c3' },
                        { label:'Total Revenue', value: `₹${(totalAmount/100000).toFixed(1)}L`, icon:'💰', color:'#8b5cf6', bg:'#f5f3ff' },
                    ].map(s => (
                        <div key={s.label} className="stat-card">
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                                <div>
                                    <div style={{ fontSize:'13px', fontWeight:'600', color:'#64748b', marginBottom:'8px' }}>{s.label}</div>
                                    <div style={{ fontSize:'28px', fontWeight:'800', color:s.color }}>{s.value}</div>
                                </div>
                                <div style={{ width:'44px', height:'44px', borderRadius:'12px', background:s.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'22px' }}>{s.icon}</div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Main Table Card */}
                <div style={{ background:'#fff', borderRadius:'16px', boxShadow:'0 4px 24px -8px rgba(0,0,0,0.08)', border:'1px solid #f1f5f9', overflow:'hidden' }}>
                    <div style={{ padding:'20px 24px', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'12px' }}>
                        <div>
                            <h2 style={{ margin:0, fontSize:'18px', fontWeight:'800', color:'#0f172a' }}>🛍️ Sales Orders</h2>
                            <p style={{ margin:'3px 0 0', fontSize:'13px', color:'#94a3b8' }}>Track and manage all your purchase orders</p>
                        </div>
                        <div style={{ display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap' }}>
                            <div style={{ position:'relative' }}>
                                <span style={{ position:'absolute', left:'10px', top:'50%', transform:'translateY(-50%)', color:'#94a3b8' }}>🔍</span>
                                <input className="search-input" placeholder="Search orders…" value={search} onChange={e => setSearch(e.target.value)} />
                            </div>
                            <button className="so-btn" style={{ background:'linear-gradient(135deg,#3b82f6,#2563eb)', color:'white', boxShadow:'0 4px 12px rgba(59,130,246,0.3)' }} onClick={addOrder}>＋ New Order</button>
                        </div>
                    </div>

                    <div style={{ padding:'12px 24px', borderBottom:'1px solid #f1f5f9', display:'flex', gap:'8px', flexWrap:'wrap' }}>
                        {['All','Draft','Confirmed','Delivered','Pending','Cancelled'].map(s => (
                            <button key={s} className={`filter-pill ${filterStatus === s ? 'active' : ''}`} onClick={() => setFilterStatus(s)}>{s}</button>
                        ))}
                    </div>

                    {loading ? (
                        <div style={{ padding:'60px', textAlign:'center', color:'#94a3b8', fontSize:'15px' }}>⏳ Loading orders from server…</div>
                    ) : (
                    <div style={{ overflowX:'auto' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse' }}>
                            <thead>
                                <tr style={{ background:'#f8fafc' }}>
                                    {['Order ID','Client','P.O. Number','Date','Delivery','Amount','Status','Actions'].map(h => (
                                        <th key={h} style={{ padding:'12px 18px', textAlign:'left', fontSize:'11px', fontWeight:'800', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.8px', whiteSpace:'nowrap' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 && (
                                    <tr><td colSpan={8} style={{ padding:'50px', textAlign:'center', color:'#94a3b8' }}>
                                        <div style={{ fontSize:'40px', marginBottom:'10px' }}>📋</div>
                                        <div style={{ fontSize:'15px', fontWeight:'600' }}>No orders found</div>
                                        <button className="so-btn" style={{ marginTop:'12px', background:'#eff6ff', color:'#3b82f6' }} onClick={addOrder}>+ Create First Order</button>
                                    </td></tr>
                                )}
                                {filtered.map(o => {
                                    const sc = STATUS_COLORS[o.status] || STATUS_COLORS.Draft;
                                    return (
                                        <tr key={o.id} className="so-row" style={{ borderBottom:'1px solid #f1f5f9', background:'#fff' }}>
                                            <td style={{ padding:'14px 18px', fontWeight:'700', color:'#3b82f6', fontSize:'13px' }}>{o.id}</td>
                                            <td style={{ padding:'14px 18px' }}>
                                                <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                                                    <div style={{ width:'32px', height:'32px', borderRadius:'8px', background:`hsl(${(o.client||'').charCodeAt(0)*13 % 360},60%,55%)`, color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:'700', fontSize:'13px', flexShrink:0 }}>{(o.client||'?').charAt(0)}</div>
                                                    <span style={{ fontWeight:'600', color:'#1e293b', fontSize:'13px' }}>{o.client}</span>
                                                </div>
                                            </td>
                                            <td style={{ padding:'14px 18px', fontSize:'13px', color:'#475569', fontWeight:'600' }}>{o.po}</td>
                                            <td style={{ padding:'14px 18px', fontSize:'13px', color:'#475569' }}>{o.date}</td>
                                            <td style={{ padding:'14px 18px', fontSize:'13px', color:'#475569' }}>{o.delivery || '—'}</td>
                                            <td style={{ padding:'14px 18px', fontSize:'13px', fontWeight:'700', color:'#0f172a' }}>₹{(o.amount||0).toLocaleString('en-IN')}</td>
                                            <td style={{ padding:'14px 18px' }}>
                                                <select value={o.status} onChange={e => updateStatus(o.id, e.target.value)}
                                                    style={{ padding:'4px 8px', borderRadius:'20px', border:'none', background:sc.bg, color:sc.color, fontWeight:'700', fontSize:'11px', cursor:'pointer', fontFamily:'Inter,sans-serif', outline:'none' }}>
                                                    {['Draft','Confirmed','Delivered','Pending','Cancelled'].map(s => <option key={s}>{s}</option>)}
                                                </select>
                                            </td>
                                            <td style={{ padding:'14px 18px' }}>
                                                <div style={{ display:'flex', gap:'6px' }}>
                                                    <button className="so-btn" style={{ padding:'5px 10px', background:'#f0fdf4', color:'#16a34a', fontSize:'12px' }} onClick={() => alert(`Convert ${o.id} → Invoice (link with Invoice page)`)}>🧾 Invoice</button>
                                                    <button className="so-btn" style={{ padding:'5px 10px', background:'#fef2f2', color:'#dc2626', fontSize:'12px' }} onClick={() => deleteOrder(o.id)}>🗑️</button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    )}

                    <div style={{ padding:'14px 24px', borderTop:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <span style={{ fontSize:'13px', color:'#94a3b8' }}>Showing {filtered.length} of {orders.length} orders • Live from backend</span>
                        <span style={{ fontSize:'13px', fontWeight:'700', color:'#0f172a' }}>Total: ₹{filtered.reduce((a,o) => a+o.amount,0).toLocaleString('en-IN')}</span>
                    </div>
                </div>
            </div>
        </>
    );
}
